import { Api, DisplayType, ModelRow, Navigation, UrlUtil } from "@mcleod/core";
import {
    Component, ContextMenuItemCreator, ContextMenuLabelProps, DataSourceAction, DataSourceExecutionEvent, Label, Switch,
    TableRowDisplayEvent, TableSelectionEvent, Toast
} from "@mcleod/components";
import { CommonDialogs, CommonTooltips } from "@mcleod/common";
import { AutogenLayoutRecoveryList } from "./autogen/AutogenLayoutRecoveryList";
import { EdiUtility } from "./EdiUtility";

export class RecoveryList extends AutogenLayoutRecoveryList {
    override async onLoad() {
        this.setSearchBordersExpanded();
        this.buttonClearSearch.enabled = false;
        this.switchPartner.allowIndeterminate = true;
        this.switchPartner.checked = undefined;
        this.buttonBypassBatch.enabled = false;
        this.buttonViewTransmissionLog.enabled = false;
        const urlParams = UrlUtil.getPropsFromUrl(window.location.href);
        if (urlParams?.recoveryEntries) {
            await this.mainDataSource.search({});
        } else if (urlParams?.ids) {
            await this.mainDataSource.search({ ids: urlParams.ids });
        } else if (urlParams?.recoveryIds) {
            await this.mainDataSource.search({ recovery_ids: urlParams.recoveryIds });
        }
        this.textboxPartnerId.requiredDuringSearch = false;
        this.textboxTransactionType.requiredDuringSearch = false;
        this.textboxMaxResults.allowDropdownBlank = false;
    }

    private setSearchBordersExpanded() {
        this.buttonExpand.imageRotation = 0;
        this.panelSearch.borderWidth = 1
        this.panelSearch.borderColor = "strokeSecondary";
        this.panelSearch.borderRadius = 4;
        this.panelSearch.borderTopWidth = 0;
        this.panelSearch.borderTopLeftRadius = 0;
        this.panelSearch.borderTopRightRadius = 0;
        this.panelSearchHeader.borderBottomWidth = 0;
        this.panelSearchHeader.borderBottomRightRadius = 0;
        this.panelSearchHeader.borderBottomLeftRadius = 0;
    }

    private setSearchBordersCollapsed() {
        this.buttonExpand.imageRotation = 90;
        this.panelSearch.borderWidth = 0;
        this.panelSearchHeader.borderBottomWidth = 1;
        this.panelSearchHeader.borderColor = "strokeSecondary";
        this.panelSearchHeader.borderBottomRightRadius = 4;
        this.panelSearchHeader.borderBottomLeftRadius = 4;
    }

    tableRecoveryOnSelect(event: TableSelectionEvent) {
        const someRowsSelected = event.table.selectedRowCount > 0;
        this.buttonBypassBatch.enabled = someRowsSelected;
        this.buttonViewTransmissionLog.enabled = someRowsSelected;
    }

    buttonClearOnClick() {
        this.mainDataSource.searchRow.clear();
        this.textboxPartnerId.clear();
        this.textboxAltPartnerId.clear();
        this.panelSearch.displayData(this.mainDataSource.searchRow, null, 0);
        this.buttonClearSearch.enabled = false;
        this.textboxPartnerId.focus();
    }

    buttonSearchExpandOnClick() {
        const transitionProps = { speed: 100, paddingTop: 0 };
        if (this.panelSearch.style.height == "0px")
            this.panelSearch.expand(transitionProps).then(() => {
                this.setSearchBordersExpanded();
            });
        else
            this.panelSearch.collapse(transitionProps).then(() => {
                this.setSearchBordersCollapsed();
            });
    }

    sourceRecoveryListBeforeExecution(event: DataSourceExecutionEvent) {
        const maxResults = this.textboxMaxResults.text as unknown as number;

        if(maxResults) {
            event.dataSource.maxResults = maxResults;
        } else {
            event.dataSource.maxResults = 1000;
        }
    }

    sourceRecoveryListAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() == DataSourceAction.SEARCH && event.dataSource.data.length > 0) {
            const transitionProps = { speed: 100, paddingTop: 0 };
            this.panelSearch.collapse(transitionProps).then(() => { this.setSearchBordersCollapsed() });
            this.panelSearchHeader.visible = true;
        }
        this.buttonClearSearch.enabled = true;
    }

    tableRecoveryOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = event.getTableRow().data as ModelRow;
        const labelFilename = tableRow.findComponentById("labelFilename") as Label;
        const labelPartnerId = tableRow.findComponentById("labelPartnerId") as Label;
        const switchOutage = tableRow.findComponentById("switchOutage") as Switch;
        switchOutage.printable = true;
        this.setTooltipContent(labelFilename, modelRow);
        this.setTooltipContent(labelPartnerId, modelRow);
        tableRow.contextMenuItems = () => this.getContextMenuItems();
    }

    setTooltipContent(component: Component, modelRow: ModelRow) {
        if (component?.id === null) return;
        switch (component.id) {
            case "labelFilename":
                return CommonTooltips.setTooltipContentTextboxes(component, () => [
                    { caption: "Filename", displayType: DisplayType.STRING, text: modelRow.get("filename") },
                    { caption: "Recovery ID", displayType: DisplayType.STRING, text: modelRow.get("id") },
                    { caption: "Transmission ID", displayType: DisplayType.STRING, text: modelRow.get("edi_log_id") },
                    { caption: "Originating Profile ID", displayType: DisplayType.STRING, text: modelRow.get("orig_profile_id") },
                ]);
            case "labelPartnerId":
                return EdiUtility.getPartnerNameTooltipForTransmission(component, modelRow, true);
        }
    }

    async bypassBatch() {
        const selectedRecoveryIds = this.selectedRecoveryIds;
        const count = selectedRecoveryIds.length;
        if (count > 0) {
            this.buttonBypassBatch.busy = true;
            this.buttonBypassBatch.busy = true;
            const beginningDialog = "Are you sure you want to bypass ";
            const endingDialog = count === 1
                ? " batch?"
                : " batches?";
            await CommonDialogs.showYesNo(beginningDialog + count + endingDialog).then(async (clickedYes) => {
                if (clickedYes) {
                    Api.post("lme/datafusion/bypass-batch", { recovery_id_list: selectedRecoveryIds }).then(result => {
                        if (result) {
                            const list: any[] = result?.data[0].bypass_batch;
                            if (list.length > 0) {
                                this.buttonSearch.clicked();
                                const message = "Data successfully bypassed.";
                                Toast.showSuccessToast(message);
                            } else {
                                const message = "No records bypassed.";
                                Toast.showSuccessToast(message);
                            }
                        }
                    });
                    this.buttonBypassBatch.busy = false;
                } else {
                    this.buttonBypassBatch.busy = false;
                }
            });
        }
    }

    viewEdiLogRecords() {
        this.buttonViewTransmissionLog.busy = true;
        const url = "lme/datafusion/TransmissionControlList?ids=" + this.selectedLogIdsString;
        Navigation.navigateTo(url, { newTab: true });
        this.buttonViewTransmissionLog.busy = false;
    }

    private get selectedRecoveryIds(): any[] {
        return this.tableRecovery.selectedRows.map(tableRow => {
            return {
                recovery_id: tableRow.data.get("id")
            };
        });
    }

    private get selectedLogIdsString(): string {
        return this.tableRecovery.selectedRows.map(tableRow => tableRow.data.get("edi_log_id")).toString();
    }

    private getContextMenuItems(): Partial<ContextMenuLabelProps>[] {
        return ContextMenuItemCreator.fromButtons(
            {button: this.buttonBypassBatch, text: "Bypass"},
            {button: this.buttonViewTransmissionLog, text: "View Transmission"}
        );
    }
}
